// useGsapConfetti.js
import { useEffect, useRef, useCallback } from "react";
import { gsap } from "gsap";
import { Physics2DPlugin } from "../gsap/Physics2DPlugin.min.js";
import { RoughEase } from "../gsap/EasePack.min.js";

// registra i plugin
gsap.registerPlugin(Physics2DPlugin, RoughEase);

export function useGsapConfetti() {
	// config originali
	const emitterSize = 375;
	const dotQuantity = 80;
	const dotSizeMin = 6;
	const dotSizeMax = 16;
	const speed = 1.4;
	const gravity = 0.7;
	const explosionQuantity = 5;

	const emitterRef = useRef(null);
	const explosionsRef = useRef([]);
	const currentExplosionRef = useRef(0);
	const moveRef = useRef(null);

	const getRandom = useCallback((min, max) => {
		return min + Math.random() * (max - min);
	}, []);

	const createExplosion = useCallback(
		(container) => {
			const tl = gsap.timeline({ paused: true });

			for (let i = 0; i < dotQuantity; i++) {
				const dot = document.createElement("div");
				dot.className = "confetto";

				const r = getRandom(30, 255);
				const g = getRandom(30, 230);
				const b = getRandom(30, 230);

				gsap.set(dot, {
					backgroundColor: `rgb(${r},${g},${b})`,
					visibility: "hidden"
				});

				const size = getRandom(dotSizeMin, dotSizeMax);
				container.appendChild(dot);

				const angle = getRandom(0.65, 0.85) * Math.PI * 2; // vettore verso l'alto
				const length = Math.random() * (emitterSize / 2 - size / 2);
				const duration = 3 + Math.random();

				// posizione iniziale
				gsap.set(dot, {
					x: Math.cos(angle) * length,
					y: Math.sin(angle) * length,
					width: size,
					height: size,
					xPercent: -50,
					yPercent: -50,
					visibility: "hidden",
					force3D: true
				});

				tl.to(
					dot,
					{
						duration: duration / 2,
						opacity: 0,
						ease: "rough({points:20, strength:1.75, clamp:true})"
					},
					0 // position parameter nel timeline
				)
					.to(
						dot,
						{
							duration,
							visibility: "visible",
							rotationX: "-=" + getRandom(720, 1440),
							rotationZ: "+=" + getRandom(720, 1440),
							physics2D: {
								angle: (angle * 180) / Math.PI,
								velocity: (100 + Math.random() * 250) * speed,
								gravity: 700 * gravity,
								friction: getRandom(0.1, 0.15)
							}
						},
						0
					)
					.to(
						dot,
						{
							duration: 1.25 + Math.random(),
							opacity: 0
						},
						duration / 2
					);
			}

			return tl;
		},
		[dotQuantity, dotSizeMin, dotSizeMax, emitterSize, speed, gravity, getRandom]
	);

	// setup iniziale
	useEffect(() => {
		const emitter = emitterRef.current;
		if (!emitter) return;

		const explosions = [];
		for (let i = 0; i < explosionQuantity; i++) {
			const container = document.createElement("div");
			container.className = "confetti-container";
			document.body.appendChild(container);

			explosions.push({
				container,
				animation: createExplosion(container)
			});
		}

		explosionsRef.current = explosions;

		// timeline per muovere l'emitter
		moveRef.current = gsap
			.timeline({ paused: true })
			.fromTo(
				emitter,
				{ duration: 0.4, left: "40%" },
				{ left: "55%", ease: "none" }
			)
			.fromTo(
				emitter,
				{ duration: 0.4, left: "55%" },
				{ left: "40%", ease: "none" }
			);

		// cleanup
		return () => {
			explosionsRef.current.forEach((e) => {
				e.animation.kill();
				if (e.container && e.container.parentNode) {
					e.container.parentNode.removeChild(e.container);
				}
			});
			explosionsRef.current = [];
			if (moveRef.current) {
				moveRef.current.kill();
			}
		};
	}, [createExplosion, explosionQuantity]);

	const explode = useCallback(() => {
		const emitter = emitterRef.current;
		const explosions = explosionsRef.current;
		if (!emitter || !explosions.length) return;

		const bounds = emitter.getBoundingClientRect();

		currentExplosionRef.current =
			(currentExplosionRef.current + 1) % explosions.length;

		const explosion = explosions[currentExplosionRef.current];

		gsap.set(explosion.container, {
			x: bounds.left + bounds.width / 2,
			y: bounds.top + bounds.height / 2
		});

		explosion.animation.restart();
	}, []);

	const fireConfetti = useCallback(() => {
		const move = moveRef.current;
		if (move) {
			move.play(0);
		}

		let intervalCount = 0;
		const interval = setInterval(() => {
			if (intervalCount < 5) {
				explode();
				intervalCount++;
			} else {
				clearInterval(interval);
			}
		}, 150);
	}, [explode]);

	return {
		emitterRef,
		fireConfetti
	};
}
